import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { resourcesAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  FolderOpen, Link2, FileText, StickyNote, Search, Plus, Pencil, Trash2,
  Download, ExternalLink, Users, Lock, Upload, ShieldCheck, Loader2,
  ListChecks, GraduationCap, Pin, UserPlus, Sparkles, Clapperboard,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResourceDocDialog } from './ResourceDocDialog';
import { ResourceFolders } from './ResourceFolders';
import { ResourceMoveMenu } from './ResourceMoveMenu';
import { ResourceAssignDialog } from './ResourceAssignDialog';
import { MemberPicker } from './MemberPicker';

const SCOPES = [
  { id: 'general', label: 'Generales', hint: 'todo el equipo', icon: Users, desc: 'Recurso compartido con todo el equipo', empty: 'Aún no hay más recursos del equipo.' },
  { id: 'direction', label: 'De dirección', hint: 'solo CEOs', icon: ShieldCheck, desc: 'Recurso de dirección: solo lo veis las CEOs', empty: 'Aún no hay recursos de dirección.' },
  { id: 'private', label: 'Propios', hint: 'solo tú', icon: Lock, desc: 'Recurso privado (solo tú)', empty: 'Aún no tienes recursos propios.' },
  { id: 'team', label: 'Del equipo', hint: 'guardados por CEOs', icon: UserPlus, desc: 'Copias guardadas en los recursos de cada persona', empty: 'Aún no habéis guardado documentos en los recursos de nadie.' },
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const TIPO_CFG = {
  link: { icon: Link2, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Enlace' },
  note: { icon: StickyNote, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Nota' },
  file: { icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Archivo' },
  checklist: { icon: ListChecks, color: 'text-indigo-600', bg: 'bg-indigo-100', label: 'Checklist' },
  doc: { icon: FileText, color: 'text-primary', bg: 'bg-primary/10', label: 'Documento' },
};
const stripHtml = (h) => (h || '').replace(/<[^>]+>/g, ' ');

const ONBOARDING_CAT = 'Onboarding / Cómo trabajamos';

const emptyForm = { id: null, scope: 'general', tipo: 'link', titulo: '', url: '', texto: '', items: '', categoria: '', tags: '', file: null, assign: [] };

// Checklist con progreso local (por usuario), sin backend de progreso
function ChecklistView({ resource, userId }) {
  const storageKey = `kd_onboard_${userId || 'anon'}_${resource.id}`;
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch { return []; }
  });
  const items = resource.items || [];
  const toggle = (i) => {
    const next = checked.includes(i) ? checked.filter((x) => x !== i) : [...checked, i];
    setChecked(next);
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* noop */ }
  };
  const done = items.filter((_, i) => checked.includes(i)).length;
  return (
    <div className="mt-1 space-y-1">
      {items.length > 0 && <p className="text-[10px] text-muted-foreground">{done}/{items.length} revisado</p>}
      {items.map((it, i) => (
        <label key={i} className="flex items-start gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={checked.includes(i)} onChange={() => toggle(i)} className="mt-0.5 accent-primary" data-testid={`checklist-${resource.id}-item-${i}`} />
          <span className={checked.includes(i) ? 'line-through text-muted-foreground' : ''}>{it}</span>
        </label>
      ))}
    </div>
  );
}

export default function ResourcesPage() {
  const { user } = useAuthStore();
  const [data, setData] = useState({ general: [], private: [], direction: null, can_edit_general: false });
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'general');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('__all');
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [openDoc, setOpenDoc] = useState(null);
  const [docEditStart, setDocEditStart] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await resourcesAPI.getAll();
      const team = user?.role === 'ceo' ? (await resourcesAPI.assigned().catch(() => ({ items: [] }))).items : null;
      setData({ general: res.general || [], private: res.private || [], direction: Array.isArray(res.direction) ? res.direction : null, team, can_edit_general: !!res.can_edit_general });
    } catch (e) {
      console.error('Error loading resources:', e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const scopes = SCOPES.filter((s) => (s.id !== 'direction' || data.direction) && (s.id !== 'team' || data.team));
  const items = data[scope] || [];
  const canEdit = scope === 'private' || scope === 'team' ? true : data.can_edit_general;
  const isCeo = user?.role === 'ceo';
  const allItems = [...data.general, ...(data.direction || []), ...data.private];
  const [assignInit, setAssignInit] = useState(null);
  useEffect(() => { if (!loading && !scopes.some((s) => s.id === scope)) setScope('general'); }, [loading, scopes, scope]);
  const copyFrom = (r) => setAssignInit({ tipo: r.tipo, titulo: r.titulo, html: r.html, url: r.url, texto: r.texto, items: r.items, categoria: '', source_id: r.id, original_filename: r.original_filename });

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((r) => { if (r.categoria) set.add(r.categoria); });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      if (catFilter !== '__all' && r.categoria !== catFilter) return false;
      if (!q) return true;
      return [r.titulo, r.texto, r.url, r.categoria, r.original_filename, stripHtml(r.html), ...(r.items || []), ...(r.tags || [])]
        .filter(Boolean).some((t) => String(t).toLowerCase().includes(q));
    });
  }, [items, search, catFilter]);

  // Onboarding va en su propio panel (solo en scope general); el resto en el grid normal.
  const onboardingItems = useMemo(
    () => (scope === 'general' ? filtered.filter((r) => r.categoria === ONBOARDING_CAT) : []),
    [filtered, scope],
  );
  const gridItems = useMemo(
    () => (scope === 'general' ? filtered.filter((r) => r.categoria !== ONBOARDING_CAT && !r.pinned) : filtered),
    [filtered, scope],
  );
  const pinnedItems = useMemo(() => (scope === 'general' ? filtered.filter((r) => r.pinned) : []), [filtered, scope]);
  const openSeed = (key) => {
    const d = allItems.find((r) => r.seed_key === key);
    if (d) { setDocEditStart(false); setOpenDoc(d); } else toast.error('Ese proceso ya no existe');
  };
  const onDocSaved = (item) => {
    setOpenDoc(item);
    const swap = (l) => (l ? l.map((r) => (r.id === item.id ? { ...r, ...item } : r)) : l);
    setData((d) => ({ ...d, general: swap(d.general), private: swap(d.private), direction: swap(d.direction), team: swap(d.team) }));
  };
  const gridCategories = useMemo(() => categories.filter((c) => c !== ONBOARDING_CAT), [categories]);

  const openAdd = (tipo, categoria = '') => {
    setForm({ ...emptyForm, scope, tipo, categoria });
    setDialog(true);
  };
  const openEdit = (r) => {
    setForm({
      id: r.id, scope, tipo: r.tipo, titulo: r.titulo || '', url: r.url || '',
      texto: r.texto || '', items: (r.items || []).join('\n'), categoria: r.categoria || '', tags: (r.tags || []).join(', '), file: null,
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.titulo.trim() && form.tipo !== 'file') { toast.error('El título es obligatorio'); return; }
    if (form.tipo === 'link' && !form.url.trim()) { toast.error('La URL es obligatoria'); return; }
    if (form.tipo === 'file' && !form.id && !form.file) { toast.error('Selecciona un archivo'); return; }
    if (form.scope === 'team' && !form.id && !form.assign.length) { toast.error('Elige al menos una persona'); return; }
    setSaving(true);
    try {
      const tagsArr = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const itemsArr = form.items.split('\n').map((t) => t.trim()).filter(Boolean);
      if (form.id) {
        await resourcesAPI.update(form.id, {
          titulo: form.titulo, url: form.url, texto: form.texto, items: itemsArr, categoria: form.categoria, tags: tagsArr,
        });
        toast.success('Recurso actualizado');
      } else if (form.assign.length && form.tipo === 'doc') {
        setDialog(false); setSaving(false);
        setAssignInit({ tipo: 'doc', titulo: form.titulo, categoria: form.categoria, html: '', user_ids: form.assign });
        return;
      } else if (form.assign.length && form.tipo !== 'file') {
        await resourcesAPI.assign({ user_ids: form.assign, tipo: form.tipo, titulo: form.titulo, url: form.url, texto: form.texto, items: itemsArr, categoria: form.categoria, tags: tagsArr });
        toast.success(`Guardado en los recursos de ${form.assign.length} persona${form.assign.length > 1 ? 's' : ''}`);
      } else if (form.tipo === 'file') {
        await resourcesAPI.upload({
          file: form.file, scope: form.scope === 'team' ? 'private' : form.scope, titulo: form.titulo || form.file.name,
          categoria: form.categoria, tags: form.tags, assignTo: form.assign,
        });
        toast.success(form.assign.length ? 'Archivo guardado en los recursos elegidos' : 'Archivo subido');
      } else {
        const res = await resourcesAPI.create({
          scope: form.scope, tipo: form.tipo, titulo: form.titulo, url: form.url,
          texto: form.texto, items: itemsArr, categoria: form.categoria, tags: tagsArr, html: form.tipo === 'doc' ? '' : undefined,
        });
        toast.success('Recurso añadido');
        if (form.tipo === 'doc' && res?.item) { setDocEditStart(true); setOpenDoc(res.item); }
      }
      setDialog(false);
      await load();
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar');
    }
    setSaving(false);
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`¿Eliminar "${r.titulo}"?`)) return;
    try {
      await resourcesAPI.remove(r.id);
      await load();
    } catch (e) { toast.error(e.message || 'No se pudo eliminar'); }
  };

  const openResource = (r) => {
    if (r.tipo === 'link' && r.url) window.open(r.url, '_blank', 'noopener');
    else if (r.tipo === 'file') window.open(resourcesAPI.downloadUrl(r.id), '_blank', 'noopener');
    else if (r.tipo === 'doc') { setDocEditStart(false); setOpenDoc(r); }
  };

  const renderCard = (r) => {
    const cfg = TIPO_CFG[r.tipo] || TIPO_CFG.note;
    const Icon = cfg.icon;
    const clickable = r.tipo === 'link' || r.tipo === 'file' || r.tipo === 'doc';
    return (
      <Card key={r.id} className="card-hover group" data-testid={`resource-${r.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
              <Icon className={`w-5 h-5 ${cfg.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <button
                  className={`text-sm font-semibold text-left truncate ${clickable ? 'hover:text-primary' : ''}`}
                  onClick={() => clickable && openResource(r)}
                  data-testid={`resource-open-${r.id}`}
                >
                  {r.titulo}
                </button>
                {canEdit && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {isCeo && <button onClick={() => copyFrom(r)} className="text-muted-foreground hover:text-primary" title="Guardar copia en los recursos de…" data-testid={`resource-assign-${r.id}`}><UserPlus className="w-3.5 h-3.5" /></button>}
                    {isCeo && scope !== 'team' && <ResourceMoveMenu resource={r} onMoved={load} />}
                    <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary" data-testid={`resource-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(r)} className="text-muted-foreground hover:text-red-500" data-testid={`resource-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
              {r.tipo === 'link' && r.url && (
                <button onClick={() => openResource(r)} className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5 truncate max-w-full">
                  <ExternalLink className="w-3 h-3 shrink-0" /> <span className="truncate">{r.url}</span>
                </button>
              )}
              {r.tipo === 'note' && r.texto && (
                <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-4">{r.texto}</p>
              )}
              {r.tipo === 'doc' && (
                <button onClick={() => openResource(r)} className="text-xs text-muted-foreground mt-1 line-clamp-2 text-left hover:text-foreground" data-testid={`resource-doc-snippet-${r.id}`}>{stripHtml(r.html).trim().slice(0, 160) || 'Documento vacío'}</button>
              )}
              {r.tipo === 'checklist' && (
                <ChecklistView resource={r} userId={user?.id} />
              )}
              {r.tipo === 'file' && (
                <button onClick={() => openResource(r)} className="text-xs text-emerald-600 hover:underline flex items-center gap-1 mt-0.5">
                  <Download className="w-3 h-3" /> {r.original_filename || 'Descargar'}
                </button>
              )}
              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                {r.assigned_by && <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/40 text-primary" data-testid={`resource-assigned-badge-${r.id}`}>{scope === 'team' ? `Para ${r.owner_name} · ` : ''}Guardado por {r.assigned_by} · {fmtDate(r.assigned_at)}</Badge>}
                {r.categoria && <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{r.categoria}</Badge>}
                {(r.tags || []).map((t, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">#{t}</Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6" data-testid="resources-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <FolderOpen className="w-7 h-7 text-primary" />
          Recursos
        </h1>
        <p className="text-muted-foreground mt-1">
          Enlaces, notas y archivos: del equipo{data.direction ? ', de dirección' : ''} y tuyos propios.
        </p>
      </div>

      <Tabs value={scope} onValueChange={(v) => { setScope(v); setCatFilter('__all'); setSearch(''); }}>
        <TabsList data-testid="resources-scope-tabs">
          {scopes.map((s) => (
            <TabsTrigger key={s.id} value={s.id} data-testid={`tab-${s.id}`}><s.icon className="w-4 h-4 mr-2" />{s.label}<span className="ml-1.5 text-[11px] text-muted-foreground hidden sm:inline">({s.hint})</span></TabsTrigger>
          ))}
        </TabsList>

        {scopes.map(({ id: sc, empty }) => (
          <TabsContent key={sc} value={sc} className="space-y-4 mt-4">
            {/* Barra de acciones */}
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar recursos..." className="pl-9" data-testid="resources-search" />
              </div>
              {canEdit && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => openAdd('doc', catFilter !== '__all' ? catFilter : '')} data-testid="add-doc-btn"><FileText className="w-4 h-4 mr-1" />Documento</Button>
                  <Button variant="outline" size="sm" onClick={() => openAdd('link')} data-testid="add-link-btn"><Link2 className="w-4 h-4 mr-1" />Enlace</Button>
                  <Button variant="outline" size="sm" onClick={() => openAdd('note')} data-testid="add-note-btn"><StickyNote className="w-4 h-4 mr-1" />Nota</Button>
                  <Button variant="outline" size="sm" onClick={() => openAdd('file')} data-testid="add-file-btn"><Upload className="w-4 h-4 mr-1" />Archivo</Button>
                  <Button variant="outline" size="sm" onClick={() => openAdd('checklist')} data-testid="add-checklist-btn"><ListChecks className="w-4 h-4 mr-1" />Checklist</Button>
                </div>
              )}
            </div>

            {/* Nota sin contraseñas */}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
              <span>No guardes contraseñas aquí. Gestiona las claves del equipo en vuestro gestor externo (Bitwarden/1Password); como mucho añade un enlace a él.</span>
            </div>

            {/* Sección Onboarding (solo en Generales) */}
            {sc === 'direction' && (
              <Link to="/resources/ganchos" className="w-full rounded-xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors px-4 py-3 flex items-center gap-3" data-testid="ad-hooks-entry">
                <Sparkles className="w-5 h-5 text-primary shrink-0" />
                <span className="flex-1"><span className="font-semibold text-sm block">Ganchos ganadores (Ads)</span><span className="text-xs text-muted-foreground">Biblioteca de ganchos en tabla, con filtros y buscador</span></span>
                <span className="text-xs text-primary">Abrir tabla</span>
              </Link>
            )}
            {sc === 'direction' && (
              <Link to="/resources/reels" className="w-full rounded-xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors px-4 py-3 flex items-center gap-3" data-testid="reels-entry">
                <Clapperboard className="w-5 h-5 text-primary shrink-0" />
                <span className="flex-1"><span className="font-semibold text-sm block">Banco de reels y TikToks virales</span><span className="text-xs text-muted-foreground">Referencias por sector y cliente, con fórmula, formato y estado</span></span>
                <span className="text-xs text-primary">Abrir tabla</span>
              </Link>
            )}
            {sc === 'general' && pinnedItems.length > 0 && (
              <div className="space-y-2" data-testid="resources-pinned">
                {pinnedItems.map((r) => (
                  <button key={r.id} onClick={() => openResource(r)} className="w-full text-left rounded-xl border-2 border-primary/40 bg-primary/5 hover:bg-primary/10 transition-colors px-4 py-3 flex items-center gap-3" data-testid={`resource-pinned-${r.id}`}>
                    <Pin className="w-4 h-4 text-primary shrink-0" />
                    <span className="font-semibold text-sm flex-1">{r.titulo}</span>
                    <span className="text-xs text-primary">Abrir índice</span>
                  </button>
                ))}
              </div>
            )}
            {sc === 'general' && (
              <Card className="border-primary/30 bg-primary/5" data-testid="onboarding-panel">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold">Onboarding / Cómo trabajamos</h3>
                    </div>
                    {canEdit && (
                      <div className="flex flex-wrap gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => openAdd('checklist', ONBOARDING_CAT)} data-testid="onboarding-add-checklist"><ListChecks className="w-4 h-4 mr-1" />Ruta/Checklist</Button>
                        <Button size="sm" variant="outline" onClick={() => openAdd('note', ONBOARDING_CAT)} data-testid="onboarding-add-note"><StickyNote className="w-4 h-4 mr-1" />Ficha/Nota</Button>
                        <Button size="sm" variant="outline" onClick={() => openAdd('link', ONBOARDING_CAT)}><Link2 className="w-4 h-4 mr-1" />Enlace</Button>
                        <Button size="sm" variant="outline" onClick={() => openAdd('file', ONBOARDING_CAT)}><Upload className="w-4 h-4 mr-1" />Archivo</Button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    Ficha operativa por rol (diseño, edición, copy…), herramientas de cada puesto y guías de los procesos de Karma. Todo lo necesario para arrancar sin depender de nadie.
                  </p>
                  {onboardingItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4" data-testid="onboarding-empty">
                      Aún no hay documentación de onboarding.{canEdit ? ' Añádela con los botones de arriba.' : ''}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="onboarding-grid">
                      {onboardingItems.map(renderCard)}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Filtro categorías */}
            {gridCategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setCatFilter('__all')} className={`text-xs px-2.5 py-1 rounded-full border ${catFilter === '__all' ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground hover:bg-muted'}`}>Todas</button>
                {gridCategories.map((c) => (
                  <button key={c} onClick={() => setCatFilter(c)} className={`text-xs px-2.5 py-1 rounded-full border ${catFilter === c ? 'bg-primary text-primary-foreground border-primary' : 'text-muted-foreground hover:bg-muted'}`} data-testid={`cat-filter-${c}`}>{c}</button>
                ))}
              </div>
            )}

            {/* Listado */}
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : gridItems.length === 0 ? (
              (sc === 'general' && onboardingItems.length > 0) ? null : (
                <div className="text-center py-16 border-2 border-dashed rounded-xl">
                  <FolderOpen className="w-12 h-12 text-muted-foreground opacity-30 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {empty}
                  </p>
                  {canEdit && <p className="text-xs text-muted-foreground mt-1">Usa los botones de arriba para añadir enlaces, notas, archivos o checklists.</p>}
                </div>
              )
            ) : sc === 'team' ? (
              <ResourceFolders items={gridItems} renderCard={renderCard} forceOpen={!!search.trim() || catFilter !== '__all'} groupBy={(r) => r.owner_name} />
            ) : sc !== 'private' ? (
              <ResourceFolders items={gridItems} renderCard={renderCard} forceOpen={!!search.trim() || catFilter !== '__all'} />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="resources-grid">
                {gridItems.map(renderCard)}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <ResourceDocDialog doc={openDoc} canEdit={openDoc?.scope === 'private' ? true : data.can_edit_general} startEditing={docEditStart}
        onClose={() => setOpenDoc(null)} onSaved={onDocSaved} onOpenSeed={openSeed}
        onAssign={isCeo ? (d, html) => { setOpenDoc(null); setAssignInit({ tipo: 'doc', titulo: d.titulo, html, categoria: '', source_id: d.id }); } : null} />
      {assignInit && <ResourceAssignDialog initial={assignInit} onClose={() => setAssignInit(null)} onDone={() => { setAssignInit(null); load(); }} />}

      {/* Diálogo crear/editar */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md" data-testid="resource-dialog">
          <DialogHeader>
            <DialogTitle>
              {form.id ? 'Editar recurso' : `Nuevo ${TIPO_CFG[form.tipo]?.label?.toLowerCase() || 'recurso'}`}
            </DialogTitle>
            <DialogDescription>
              {SCOPES.find((s) => s.id === form.scope)?.desc}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label>Título</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Nombre del recurso" data-testid="resource-title-input" />
            </div>
            {form.tipo === 'link' && (
              <div>
                <Label>URL</Label>
                <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." data-testid="resource-url-input" />
              </div>
            )}
            {form.tipo === 'note' && (
              <div>
                <Label>Texto</Label>
                <Textarea value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} rows={4} placeholder="Escribe la nota..." data-testid="resource-text-input" />
              </div>
            )}
            {form.tipo === 'checklist' && (
              <div>
                <Label>Pasos del checklist (uno por línea)</Label>
                <Textarea value={form.items} onChange={(e) => setForm({ ...form, items: e.target.value })} rows={6} placeholder={'Leer la guía de marca\nConfigurar acceso a Canva\nRevisar el flujo de aprobación...'} data-testid="resource-items-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Cada línea es un paso marcable. Ideal para rutas de onboarding por rol.</p>
              </div>
            )}
            {form.tipo === 'file' && !form.id && (
              <div>
                <Label>Archivo (máx 25 MB)</Label>
                <Input type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="resource-file-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoría (opcional)</Label>
                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="Ej: Herramientas" data-testid="resource-category-input" />
              </div>
              <div>
                <Label>Etiquetas (coma)</Label>
                <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="canva, diseño" data-testid="resource-tags-input" />
              </div>
            </div>
            {isCeo && !form.id && (
              <div className="rounded-lg border border-dashed p-3 space-y-2" data-testid="resource-assign-section">
                <Label className="flex items-center gap-1.5"><UserPlus className="w-4 h-4 text-primary" />Guardar en los recursos de…{form.scope === 'team' ? '' : ' (opcional)'}</Label>
                <MemberPicker value={form.assign} onChange={(v) => setForm({ ...form, assign: v })} testId="resource-form-members" />
                {form.assign.length > 0 && <p className="text-[11px] text-muted-foreground">Cada persona recibe su propia copia independiente en sus recursos (no se guarda aquí).{form.tipo === 'doc' ? ' Al continuar podrás rellenar el contenido.' : ''}</p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-resource-btn">
              {saving ? 'Guardando...' : (form.id ? 'Guardar' : 'Añadir')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
